"use client"


import { Button } from "../components/ui/button"
import { X } from "lucide-react"
import { useState } from "react"
import { Copy, RefreshCw } from "lucide-react"
import Switch from "./Switch"
import { useNavigate } from "react-router-dom"
import useSWRMutation from "swr/mutation";
import { InputNumber, message, Radio } from "antd"
import { handleMutation, handleUpdates } from "../util/axiosConfig";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import ConfirmModal from "../components/modal/ConfirmModal"
import { cn } from "../lib/utils"
import requestErrorHandler from "../util/requestErrorHandler"
import { useCurrency } from "../hooks/useCurrency"

export function PartnerDetailsCard({ selectedUser, onSubmit }:{ selectedUser: Partner, onSubmit: () => void }) {
  const [apiKey, setApiKey] = useState(selectedUser?.apiKey || '')
  const [switchValue, setSwitchValue] = useState<boolean>(selectedUser?.status === 'active')
  const [hasDiscount, setHasDiscount] = useState<boolean>(selectedUser?.hasDiscount);
  const navigate = useNavigate()
  const [discount, setDiscount] = useState({
    amount: '',
    type: ''
  });
  const [error, setError] = useState('');
  const { formatLargeNumber } = useCurrency();

  const { trigger: regenerate, isMutating: regenerating } = useSWRMutation("newPartner/generate-new-api-key", handleMutation);
  const { trigger: triggerRevoke, isMutating: isRevoking } = useSWRMutation("newPartner/revoke-api-key", handleMutation);
  const { trigger: toggleDiscount, isMutating } = useSWRMutation("newPartner/toggle-discount", handleMutation);
  const { trigger: updateDiscount, isMutating: updatingDiscount } = useSWRMutation(`newPartner/update-discount/${selectedUser.id}`, handleUpdates);
  const { trigger: toggleStatus, isMutating: updatingStatus } = useSWRMutation(`newAdmin/users/partner/${selectedUser?.id}/toggle-status`, handleMutation);

  const copyApiKey = () => {
    if (!apiKey) return;
    navigator.clipboard.writeText(apiKey);
    message.success("API Key copied to clipboard");
  }

  const regenerateKey =  async () => {
    if (!selectedUser) return;
    try {
      const response = await regenerate({
        partnerId: selectedUser?.id
      });
      setApiKey(response.data.apiKey);
      message.success("API Key regenerated");
      onSubmit()
    } catch (error) {
      requestErrorHandler(error);
    }
  }

  const revokeKey =  async () => {
    if (!selectedUser) return;
    try {
      await triggerRevoke({
        partnerId: selectedUser?.id
      });
      message.success("API Key revoked");
      setApiKey("");
      onSubmit();
    } catch (error) {
      requestErrorHandler(error);
    }
  }

  const updateDiscountValue =  async () => {
    if (!selectedUser || !discount.amount || !discount.type) {
      setError('Missing required fields')
      return;
    }

    const payload: any = {};

    if (discount.type === 'percentage' && Number(discount.amount) > 100) {
      setError('Percentage should be 1 - 100');
      return;
    }
    setError('');

    if (discount.type === 'percentage') {
      payload.discount = discount.amount;
    } else {
      payload.flatRate = discount.amount
    }

    try {
      const response = await updateDiscount(payload);
      message.success("Discount value updated");
      onSubmit()
    } catch (error) {
      console.error("Error updating discount value:", error);
    }
  }  

  const toggleDiscountStatus =  async () => {
    if (!selectedUser) return;
    try {
      const response = await toggleDiscount({
        partnerId: selectedUser?.id
      });
      message.success("Discount status updated");
      onSubmit();
    } catch (error) {
      console.error("Error updating discount status:", error);
    }
  }

  const toggleSwitch =  async () => {
    if (!selectedUser) return;
    try {
      const response = await toggleStatus({
        status: switchValue ? "inactive" : "active",
      });
      message.success("User Status updated");
      onSubmit();
    } catch (error) {
      console.error("Error updating status:", error);
    }
  }

  const goToTransactions = () => {
    navigate(`/admin/users/${selectedUser.id}/partner/transactions`);
  }

  return (
    <div className="border rounded-lg overflow-hidden bg-white">
      <div className="bg-blue-200 p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-blue-800 flex items-center justify-center">
          <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M15 9C15 7.34315 13.6569 6 12 6C10.3431 6 9 7.34315 9 9C9 10.6569 10.3431 12 12 12C13.6569 12 15 13.3431 15 15C15 16.6569 13.6569 18 12 18C10.3431 18 9 16.6569 9 15"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h2 className="text-xl font-medium text-blue-800 capitalize">{selectedUser.businessName}</h2>
      </div>

      <div className="p-5 space-y-5">
        <div>
          <div className="text-gray-700 font-medium mb-1">Status</div>
          <div className="flex items-center gap-2">
            <Switch
              switchValue={switchValue}
              setSwitchValue={(value: any) => {
                setSwitchValue(value);
                void toggleSwitch()
              }}
            />
            {
              switchValue
              ? <span className="text-blue-600 font-medium">Active</span>
              : <span className=" text-gray-400 font-medium">Inactive</span>
            }
            {updatingStatus && <LoadingSpinner color="grey" />}
          </div>
        </div>

        <div>
          <div className="text-gray-500">{selectedUser?.email}</div>
        </div>

        <div className="flex items-center gap-1">
          <span className="text-blue-600 font-bold">{selectedUser?.totalTripsCompleted}/{selectedUser?.totalTripsCreated}</span>
          <span className="text-gray-500 text-sm">Trips Completed</span>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col justify-center items-center">
            <div className="text-4xl font-bold text-gray-800 uppercase">{formatLargeNumber(selectedUser?.totalAmountOwed || 0)}</div>
            <div className="text-gray-500 text-sm">Amount Owed</div>
          </div>
          <div className="flex flex-col justify-center items-center">
            <div className="text-4xl font-bold text-gray-800 uppercase">{formatLargeNumber(selectedUser?.limit || 0)}</div>
            <div className="text-gray-500 text-sm">Request Limit</div>
          </div>
        </div>

        <div>
          <div className="text-blue-600">
            {selectedUser.businessAddress}
          </div>
        </div>

        <div>
          <h3 className="text-primary mb-2">
            Discount
          </h3>
          <div className="flex items-center justify-between gap-2">
            <div className={cn("flex gap-2", selectedUser.hasDiscount && "flex-col")}>
                <Switch
                  switchValue={hasDiscount}
                  setSwitchValue={(value: any) => {
                    setHasDiscount(value)
                    void toggleDiscountStatus()
                  }}
                />
                {
                  !hasDiscount
                  ? <span className="text-blue-600 text-sm font-medium">Enable</span>
                  : <span className=" text-red-500 text-sm font-medium">Disable</span>
                }
                {isMutating && <LoadingSpinner color="grey" />}
            </div>
            {selectedUser.hasDiscount && (
              <div className="flex flex-col gap-2">
                {(selectedUser.flatRate && selectedUser.flatRate !== '0.00')
                  ? <p className="text-2xl font-bold text-gray-800 uppercase">{selectedUser.flatRate}</p>
                  : <p className="text-2xl font-bold text-gray-800 uppercase">{selectedUser.discount ? `${selectedUser.discount} %`: ''}</p>
                }
                <p className="text-sm font-medium">Discount Value</p>
              </div>       
            )}
          </div>
          <div className="flex items-center justify-between gap-4 mt-6">
            <ConfirmModal
              title="Set Discount Value"
              triggerLabel={
                Number(selectedUser?.discount) || Number(selectedUser?.flatRate)
                ? "Update value"
                : "Set Discount"
              }
              confirmLabel="Submit"
              triggerClassName="text-white"
              onConfirm={updateDiscountValue}
              disabled={updatingDiscount || !hasDiscount}
              loading={updatingDiscount}
            >
                <div className="space-y-3 mt-4">
                    <div className="flex flex-col gap-2">
                      <label htmlFor="discount" className="font-medium text-sm">
                        Discount Value
                      </label>
                      <InputNumber
                          value={discount.amount}
                          name="amount"
                          placeholder="E.g 1 - 100 or 2000"
                          className="!border !border-gray-500 !text-black !font-bold w-full"
                          onChange={(val) => setDiscount({...discount, amount: val || '' })}
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label htmlFor="discount" className="font-medium text-sm">
                          Discount Type
                      </label>
                      <Radio.Group
                          className="flex flex-col gap-1"
                          value={discount.type}
                          options={[
                              {value: 'percentage', label: 'Discount Percentage'},
                              {value: 'flatRate', label: 'Flat Rate'}
                          ]}
                          onChange={(e) => setDiscount({...discount, type: e.target.value})}
                      />
                      {error && (<p className="text-sm text-center text-red-500 font-medium">{error}</p>)}
                    </div>
                </div>
            </ConfirmModal>
          </div>
        </div>

        <div className="flex items-center justify-between bg-gray-50 p-2 rounded">
          <div className="text-amber-500 font-mono text-sm truncate max-w-[200px]">{apiKey || 'No API Key'}</div>
          <Button className="bg-transparent" variant="ghost" size="icon" onClick={copyApiKey}>
            <Copy className="h-4 w-4" />
            <span className="sr-only">Copy API key</span>
          </Button>
        </div>

        <div>
          <Button
            variant="ghost"
            className="bg-transparent flex items-center gap-1 p-0 hover:bg-transparent hover:text-blue-600"
            disabled={regenerating}
            onClick={regenerateKey}
          >
            <RefreshCw className="h-4 w-4" />
            <span>Regenerate key {regenerating && <LoadingSpinner color="grey" />}</span>
          </Button>
          <Button className="w-full text-white mt-4" onClick={revokeKey}>
            Revoke API Key {isRevoking && <LoadingSpinner />}
          </Button>
        </div>

        <div>
          <Button variant="link" className=" bg-transparent text-amber-500 p-0 underline" onClick={() => goToTransactions()}>
            View Transactions
          </Button>
        </div>

        {/* <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">Save</Button> */}
      </div>
    </div>
  )
}


function ManagePartner({
  selectedUser,
  handleClosePanel,
  onSubmit
}: { selectedUser: Partner, handleClosePanel: () => void, onSubmit: () => void }) {
  return (
    <div key={selectedUser?.id} className="bg-white rounded-lg shadow p-6 transition-all duration-300">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-blue-600">Manage Partner</h2>
        <Button variant="ghost" size="icon" onClick={handleClosePanel} className="hover:bg-gray-100">
          <X className="h-5 w-5 text-red-500" />
        </Button>
      </div>


      <PartnerDetailsCard selectedUser={selectedUser} onSubmit={onSubmit} />
    </div>
  );
}

export default ManagePartner;